// "Live" without a socket (BA v1.2: FCM is the only realtime channel):
//  * tab badges: one counts fetch on start, on resume/visibility and on tab change
//  * push messages (Android plugin or web FCM onMessage) carry data.counts → badges
//    right away, plus an in-app banner that opens data.url

import { h } from './dom';
import { avatar } from './components';
import { clearCounts, currentCounts, onCounts, refreshCounts, setCounts } from './shell';
import { currentPath, navigate } from './router';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import type { Counts } from '../api/client';
import { emojiLine, parseReaction, reactionSentence } from './reaction-text';

/** What both push transports hand us (FCM: notification + string-only data). */
export interface PushMessage {
  title?: string;
  body?: string;
  data?: Record<string, string | undefined>;
}

const pushListeners = new Set<(m: PushMessage) => void>();
/** Screens can react to a push (e.g. the post page reloads its comments). */
export const onPush = (fn: (m: PushMessage) => void) => { pushListeners.add(fn); return () => pushListeners.delete(fn); };

let started = false;
const cleanups: (() => void)[] = [];

export function startLive() {
  void refreshCounts(true);
  if (started) return;
  started = true;
  const vis = () => { if (document.visibilityState === 'visible') void refreshCounts(true); };
  document.addEventListener('visibilitychange', vis);
  cleanups.push(() => document.removeEventListener('visibilitychange', vis));
  if (Capacitor.isNativePlatform()) {
    void App.addListener('resume', () => void refreshCounts(true)).then((l) => {
      if (!started) return void l.remove();
      cleanups.push(() => void l.remove());
    }).catch(() => {});
  }
  // installed web app: mirror the counts on the app icon when the browser supports it
  cleanups.push(onCounts(appBadge));
}

export function stopLive() {
  started = false;
  cleanups.splice(0).forEach((fn) => fn());
  clearCounts();
  appBadge({ bell: 0, friends: 0, rooms: 0, feed: 0 });
}

function appBadge(c: Counts) {
  const n = c.bell + c.friends + c.rooms;
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
  try {
    if (n > 0) void nav.setAppBadge?.(n)?.catch(() => {});
    else void nav.clearAppBadge?.()?.catch(() => {});
  } catch { /* unsupported */ }
}

/** `data.counts` is a JSON string (FCM data values are strings). */
export function parseCounts(raw: unknown): Partial<Counts> | null {
  if (!raw) return null;
  try {
    const c = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return c && typeof c === 'object' ? (c as Partial<Counts>) : null;
  } catch { return null; }
}

/** Only our own hash routes may be opened from a notification. */
export const safeRoute = (u: unknown) => (typeof u === 'string' && /^#\/[\w\-/?=&%.]*$/.test(u) ? u : null);

/** A push arrived while the app is in the foreground. */
export function handlePush(m: PushMessage) {
  const d = m.data ?? {};
  setCounts(parseCounts(d.counts));
  if (d.postId && d.commentCount && Number.isFinite(Number(d.commentCount))) setCommentCount(d.postId, Number(d.commentCount));
  pushListeners.forEach((fn) => { try { fn(m); } catch (e) { console.error(e); } });
  const url = safeRoute(d.url);
  // already looking at the thing the notification is about: the screen refreshes itself
  if (url && `#${currentPath()}` === url.split('?')[0]) return;
  banner(m, url);
}

/** Updates "💬 N bình luận" on every rendered card of this post. */
export function setCommentCount(postId: string, n: number) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return;
  document.querySelectorAll<HTMLElement>(`[data-post-id="${CSS.escape(postId)}"] [data-comment-count]`).forEach((el) => {
    el.dataset.commentCount = String(n);
    el.textContent = n ? `💬 ${n} bình luận` : '💬 Bình luận';
  });
}

let current: HTMLElement | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;

function banner(m: PushMessage, url: string | null) {
  const d = m.data ?? {};
  let title = m.title || d.title || 'Booth Together';
  let body = m.body || d.body || '';
  let actor = d.actorId ? { id: d.actorId, displayName: d.actorName ?? title, avatarUrl: d.actorAvatar ?? null } : null;
  if (d.kind === 'REACTION') {
    // merged per post: same wording as the notifications screen
    const r = parseReaction(d, { id: d.actorId ?? null, name: d.actorName, avatarUrl: d.actorAvatar ?? null });
    title = reactionSentence(r);
    body = emojiLine(r.emojis);
    const a = r.actors[0];
    if (a) actor = { id: a.id ?? a.name, displayName: a.name, avatarUrl: a.avatarUrl };
  }
  current?.remove();
  clearTimeout(timer);
  const el = h(url ? 'button.livebanner' : 'div.livebanner', {
    role: 'status',
    onclick: url ? () => { el.remove(); navigate(url); } : null,
  },
  actor ? avatar(actor, 38) : h('span.lb-icon', { 'aria-hidden': 'true' }, '🔔'),
  h('span.lb-text', {}, h('b', {}, title), body ? h('small', {}, body) : null),
  url ? h('span.lb-x', { 'aria-hidden': 'true' }, '›') : null);
  document.body.append(el);
  current = el;
  requestAnimationFrame(() => el.classList.add('show'));
  timer = setTimeout(() => {
    el.classList.remove('show');
    setTimeout(() => el.remove(), 300);
  }, 5000);
}

// Dev only (stripped from production builds): simulate a push from the mock server.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as { __boothMockPush: (kind?: string) => Promise<PushMessage> }).__boothMockPush = async (kind?: string) => {
    const { accessToken } = await import('../auth/oidc');
    const r = await fetch(`/api/_mock/push${kind ? `?kind=${encodeURIComponent(kind)}` : ''}`, { method: 'POST', headers: { authorization: `Bearer ${await accessToken()}` } });
    const m = (await r.json()) as PushMessage;
    handlePush(m);
    return m;
  };
  void currentCounts;
}
